export const FILE_SIZE_LIMITS = {
  DEFAULT_MAX_SIZE: 100 * 1024 * 1024, // 100MB
  UPLOAD_LINK_DEFAULT: 50 * 1024 * 1024, // 50MB
} as const;

export const STORAGE_LIMITS = {
  DEFAULT_USER_LIMIT: 5 * 1024 * 1024 * 1024, // 5GB (Starter default)
} as const;

// Plan definitions
export const PLANS = {
  STARTER: {
    id: 'starter',
    name: 'Starter',
    price: 9.99,
    storage: 5 * 1024 * 1024 * 1024, // 5GB
    clients: 10,
    diagnostics: 5,
    aiUsesPerMonth: 50,
    users: 1,
    portalBranding: false,
    prioritySupport: false,
  },
  PRO: {
    id: 'pro',
    name: 'Pro',
    price: 19.99,
    storage: 15 * 1024 * 1024 * 1024, // 15GB
    clients: -1, // unlimited
    diagnostics: -1, // unlimited
    aiUsesPerMonth: -1, // unlimited (fair use)
    users: 1,
    portalBranding: true,
    prioritySupport: true,
  },
} as const;

// AI Fair Use Policy limits (for Pro plan)
export const AI_FAIR_USE = {
  DAILY_LIMIT: 100, // Max AI requests per day
  HOURLY_LIMIT: 30, // Max AI requests per hour
  MONTHLY_SOFT_LIMIT: 1000, // Soft limit before review
  MAX_INPUT_TOKENS: 10000, // Max tokens per request input
  MAX_OUTPUT_TOKENS: 4000, // Max tokens per request output
} as const;

// Google Play subscription products
export const GOOGLE_PLAY_PRODUCTS = {
  STARTER_MONTHLY: 'affogato_starter_monthly',
  STARTER_YEARLY: 'affogato_starter_yearly',
  PRO_MONTHLY: 'affogato_pro_monthly',
  PRO_YEARLY: 'affogato_pro_yearly',
} as const;

// Map Google Play product IDs to plan tiers
export const PRODUCT_TO_PLAN: Record<string, { tier: 'starter' | 'pro'; cycle: 'monthly' | 'yearly' }> = {
  [GOOGLE_PLAY_PRODUCTS.STARTER_MONTHLY]: { tier: 'starter', cycle: 'monthly' },
  [GOOGLE_PLAY_PRODUCTS.STARTER_YEARLY]: { tier: 'starter', cycle: 'yearly' },
  [GOOGLE_PLAY_PRODUCTS.PRO_MONTHLY]: { tier: 'pro', cycle: 'monthly' },
  [GOOGLE_PLAY_PRODUCTS.PRO_YEARLY]: { tier: 'pro', cycle: 'yearly' },
} as const;

// Google Play RTDN notification types
export const GOOGLE_NOTIFICATION_TYPES = {
  SUBSCRIPTION_RECOVERED: 1,
  SUBSCRIPTION_RENEWED: 2,
  SUBSCRIPTION_CANCELED: 3,
  SUBSCRIPTION_PURCHASED: 4,
  SUBSCRIPTION_ON_HOLD: 5,
  SUBSCRIPTION_IN_GRACE_PERIOD: 6,
  SUBSCRIPTION_RESTARTED: 7,
  SUBSCRIPTION_PRICE_CHANGE_CONFIRMED: 8,
  SUBSCRIPTION_DEFERRED: 9,
  SUBSCRIPTION_PAUSED: 10,
  SUBSCRIPTION_PAUSE_SCHEDULE_CHANGED: 11,
  SUBSCRIPTION_REVOKED: 12,
  SUBSCRIPTION_EXPIRED: 13,
} as const;

// Subscription grace period (days)
export const SUBSCRIPTION_GRACE_PERIOD_DAYS = 3;

export const JWT_CONFIG = {
  DEFAULT_EXPIRES_IN: '7d',
  REFRESH_THRESHOLD: 24 * 60 * 60 * 1000, // 24 hours in ms
} as const;

export const PRESIGNED_URL_EXPIRY = {
  UPLOAD: 3600, // 1 hour
  DOWNLOAD: 3600, // 1 hour
  PUBLIC_DOWNLOAD: 1800, // 30 minutes
} as const;

export const MIME_TYPE_ICONS: Record<string, string> = {
  'application/pdf': 'document',
  'application/msword': 'document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'document',
  'application/vnd.ms-excel': 'spreadsheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'spreadsheet',
  'application/vnd.ms-powerpoint': 'presentation',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'presentation',
  'text/plain': 'text',
  'text/html': 'code',
  'text/css': 'code',
  'text/javascript': 'code',
  'application/json': 'code',
  'application/zip': 'archive',
  'application/x-rar-compressed': 'archive',
  'application/x-7z-compressed': 'archive',
  'image/jpeg': 'image',
  'image/png': 'image',
  'image/gif': 'image',
  'image/webp': 'image',
  'image/svg+xml': 'image',
  'video/mp4': 'video',
  'video/webm': 'video',
  'video/quicktime': 'video',
  'audio/mpeg': 'audio',
  'audio/wav': 'audio',
  'audio/ogg': 'audio',
} as const;

export const FILE_EXTENSIONS_BY_TYPE: Record<string, string[]> = {
  document: ['.pdf', '.doc', '.docx', '.txt', '.rtf', '.odt'],
  spreadsheet: ['.xls', '.xlsx', '.csv', '.ods'],
  presentation: ['.ppt', '.pptx', '.odp'],
  image: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.ico'],
  video: ['.mp4', '.webm', '.mov', '.avi', '.mkv'],
  audio: ['.mp3', '.wav', '.ogg', '.flac', '.aac'],
  archive: ['.zip', '.rar', '.7z', '.tar', '.gz'],
  code: ['.js', '.ts', '.jsx', '.tsx', '.html', '.css', '.json', '.xml', '.yml', '.yaml'],
} as const;

export const FOLDER_COLORS = [
  '#6366f1', // indigo
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#14b8a6', // teal
  '#06b6d4', // cyan
  '#3b82f6', // blue
] as const;
